/**
 * Качественный детектор движения на основе анализа видеопотока
 * Использует Canvas API для анализа изменений между кадрами
 */

export interface MotionDetectorOptions {
  threshold?: number // Порог чувствительности (0-255, по умолчанию 30)
  sampleRate?: number // Частота анализа кадров в мс (по умолчанию 200)
  minMotionFrames?: number // Минимальное количество кадров с движением для активации (по умолчанию 3)
  region?: {
    x: number
    y: number
    width: number
    height: number
  } // Область анализа (по умолчанию весь кадр)
}

export class MotionDetector {
  private video: HTMLVideoElement | null = null
  private canvas: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null
  private previousFrame: ImageData | null = null
  private animationFrameId: number | null = null
  private stream: MediaStream | null = null
  
  private options: Required<MotionDetectorOptions>
  private motionFramesCount = 0
  private isRunning = false
  
  private onMotionDetected: (() => void) | null = null

  constructor(options: MotionDetectorOptions = {}) {
    this.options = {
      threshold: options.threshold ?? 30,
      sampleRate: options.sampleRate ?? 200,
      minMotionFrames: options.minMotionFrames ?? 3,
      region: options.region ?? { x: 0, y: 0, width: 0, height: 0 },
    }
  }

  /**
   * Запускает детектор движения
   */
  async start(onMotionDetected: () => void): Promise<void> {
    if (this.isRunning) {
      return
    }

    this.onMotionDetected = onMotionDetected

    try {
      // Получаем доступ к камере для детекции движения
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user', // Фронтальная камера по умолчанию
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      })

      // Создаем скрытый video элемент для анализа
      this.video = document.createElement('video')
      this.video.srcObject = this.stream
      this.video.playsInline = true
      this.video.muted = true
      this.video.style.display = 'none'
      this.video.style.position = 'fixed'
      this.video.style.top = '-9999px'
      document.body.appendChild(this.video)

      await this.video.play()

      // Создаем canvas для анализа кадров.
      // Используем уменьшенное разрешение 160×120 вместо полного видео:
      // это снижает нагрузку на CPU в 16 раз (307 200 → 19 200 пикселей) при
      // сохранении точности обнаружения движения.
      this.canvas = document.createElement('canvas')
      this.canvas.width = 160
      this.canvas.height = 120
      this.ctx = this.canvas.getContext('2d', { willReadFrequently: true })

      if (!this.ctx) {
        throw new Error('Failed to get canvas context')
      }

      // Устанавливаем область анализа если явно указана
      if (this.options.region.width > 0 && this.options.region.height > 0) {
        this.canvas.width = this.options.region.width
        this.canvas.height = this.options.region.height
      }

      this.isRunning = true
      this.motionFramesCount = 0
      this.previousFrame = null

      // Запускаем анализ
      this.analyze()
    } catch (error) {
      console.error('Motion detector start error:', error)
      // Не вызываем stop() чтобы избежать ошибки AbortError
      // Просто очищаем состояние
      this.isRunning = false
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId)
        this.animationFrameId = null
      }
      throw error
    }
  }

  /**
   * Останавливает детектор движения
   */
  stop(): void {
    this.isRunning = false

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }

    if (this.video) {
      this.video.pause()
      this.video.srcObject = null
      if (this.video.parentNode) {
        this.video.parentNode.removeChild(this.video)
      }
      this.video = null
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }

    if (this.canvas) {
      this.canvas = null
    }

    this.ctx = null
    this.previousFrame = null
    this.motionFramesCount = 0
    this.onMotionDetected = null
  }

  /**
   * Анализирует кадры на предмет движения
   */
  private analyze(): void {
    if (!this.isRunning || !this.video || !this.ctx || !this.canvas) {
      return
    }

    const now = Date.now()
    const lastAnalysisTime = (this as any).lastAnalysisTime || 0

    if (now - lastAnalysisTime >= this.options.sampleRate) {
      ;(this as any).lastAnalysisTime = now

      try {
        // Рисуем текущий кадр на canvas
        if (this.options.region.width > 0 && this.options.region.height > 0) {
          this.ctx.drawImage(
            this.video,
            this.options.region.x,
            this.options.region.y,
            this.options.region.width,
            this.options.region.height,
            0,
            0,
            this.canvas.width,
            this.canvas.height
          )
        } else {
          this.ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height)
        }

        // Получаем данные текущего кадра
        const currentFrame = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height)

        // Сравниваем с предыдущим кадром
        if (this.previousFrame) {
          const motionDetected = this.detectMotion(this.previousFrame, currentFrame)

          if (motionDetected) {
            this.motionFramesCount++
            // Если движение обнаружено в нескольких кадрах подряд - активируем
            if (this.motionFramesCount >= this.options.minMotionFrames) {
              this.onMotionDetected?.()
              this.motionFramesCount = 0 // Сбрасываем счетчик
            }
          } else {
            this.motionFramesCount = Math.max(0, this.motionFramesCount - 1)
          }
        }

        this.previousFrame = currentFrame
      } catch (error) {
        console.warn('Motion analysis error:', error)
      }
    }

    this.animationFrameId = requestAnimationFrame(() => this.analyze())
  }

  /**
   * Определяет движение между двумя кадрами
   */
  private detectMotion(frame1: ImageData, frame2: ImageData): boolean {
    if (frame1.data.length !== frame2.data.length) {
      return false
    }

    let diffPixels = 0
    const totalPixels = frame1.data.length / 4 // RGBA = 4 байта на пиксель

    // Анализируем только яркость (Y канал) для производительности
    for (let i = 0; i < frame1.data.length; i += 4) {
      // Конвертируем RGB в яркость (Y = 0.299*R + 0.587*G + 0.114*B)
      const brightness1 =
        0.299 * frame1.data[i] + 0.587 * frame1.data[i + 1] + 0.114 * frame1.data[i + 2]
      const brightness2 =
        0.299 * frame2.data[i] + 0.587 * frame2.data[i + 1] + 0.114 * frame2.data[i + 2]

      const diff = Math.abs(brightness1 - brightness2)

      if (diff > this.options.threshold) {
        diffPixels++
      }
    }

    // Движение обнаружено если изменилось более 5% пикселей (было 2% - слишком чувствительно для планшетов)
    const motionPercentage = (diffPixels / totalPixels) * 100
    return motionPercentage > 5
  }

  /**
   * Проверяет доступность API для детекции движения
   */
  static isAvailable(): boolean {
    return !!(
      typeof navigator !== 'undefined' &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      typeof HTMLCanvasElement !== 'undefined' &&
      typeof HTMLCanvasElement.prototype.getContext === 'function'
    )
  }
}

